import { Flex, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CSSProperties, ReactNode, useId } from 'react';
import styled from 'styled-components';

export type CustomSpinnerVariant = 'simple' | 'animated';

export type CustomSpinnerProps = {
  /** Visual variant of the loading indicator */
  variant?: CustomSpinnerVariant;
  /** Width of the animated spinner (only for 'animated' variant) */
  width?: number;
  /** Color of the spinner */
  color?: string;
  /** Show branded logo in center (only for 'animated' variant) */
  showLogo?: boolean;
  /** Primary text to show below logo */
  primaryText?: string;
  /** Secondary text to show below primary text */
  secondaryText?: string;
  /** Custom logo element to display in center */
  logo?: ReactNode;
  /** Custom styles for the container */
  style?: CSSProperties;
  /** Custom class name */
  className?: string;
};

const AnimatedSpinner = ({
  width = 200,
  color = '#000',
}: {
  width?: number;
  color?: string;
}) => {
  const id = useId();
  const gradientId = `custom-spinner-gradient-${id}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      width={width}
      height={width}
    >
      <radialGradient
        id={gradientId}
        cx=".66"
        fx=".66"
        cy=".3125"
        fy=".3125"
        gradientTransform="scale(1.5)"
      >
        <stop offset="0" stopColor={color} />
        <stop offset=".3" stopColor={color} stopOpacity=".9" />
        <stop offset=".6" stopColor={color} stopOpacity=".6" />
        <stop offset=".8" stopColor={color} stopOpacity=".3" />
        <stop offset="1" stopColor={color} stopOpacity="0" />
      </radialGradient>
      <circle
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="200 1000"
        strokeDashoffset="0"
        cx="100"
        cy="100"
        r="70"
        style={{ transformOrigin: 'center' }}
      >
        <animateTransform
          type="rotate"
          attributeName="transform"
          calcMode="spline"
          dur="2"
          values="360;0"
          keyTimes="0;1"
          keySplines="0 0 1 1"
          repeatCount="indefinite"
        />
      </circle>
      <circle
        fill="none"
        opacity=".2"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        cx="100"
        cy="100"
        r="70"
      />
    </svg>
  );
};

const DefaultLogo = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g data-name="Product Icons">
      <g>
        <polygon
          opacity="1"
          points="16.64 15.13 17.38 13.88 20.91 13.88 22 12 19.82 8.25 16.75 8.25 15.69 6.39 14.5 6.39 14.5 5.13 16.44 5.13 17.5 7 19.09 7 16.9 3.25 12.63 3.25 12.63 8.25 14.36 8.25 15.09 9.5 12.63 9.5 12.63 12 14.89 12 15.94 10.13 18.75 10.13 19.47 11.38 16.67 11.38 15.62 13.25 12.63 13.25 12.63 17.63 16.03 17.63 15.31 18.88 12.63 18.88 12.63 20.75 16.9 20.75 20.18 15.13 18.09 15.13 17.36 16.38 14.5 16.38 14.5 15.13 16.64 15.13"
        />
        <polygon points="7.36 15.13 6.62 13.88 3.09 13.88 2 12 4.18 8.25 7.25 8.25 8.31 6.39 9.5 6.39 9.5 5.13 7.56 5.13 6.5 7 4.91 7 7.1 3.25 11.38 3.25 11.38 8.25 9.64 8.25 8.91 9.5 11.38 9.5 11.38 12 9.11 12 8.06 10.13 5.25 10.13 4.53 11.38 7.33 11.38 8.38 13.25 11.38 13.25 11.38 17.63 7.97 17.63 8.69 18.88 11.38 18.88 11.38 20.75 7.1 20.75 3.82 15.13 5.91 15.13 6.64 16.38 9.5 16.38 9.5 15.13 7.36 15.13" />
      </g>
    </g>
  </svg>
);

const Container = styled.div`
  position: relative;
  text-align: center;
`;

const CenteredContent = styled(Flex)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  flex-direction: column;
  align-items: center;
`;

/**
 * Custom spinner component with multiple visual variants.
 *
 * Provides two visual variants:
 * - `simple`: Basic Ant Design spinner
 * - `animated`: Custom SVG spinner with optional centered logo and text
 *
 * @param props - Component props
 * @returns Spinner element
 *
 * @example
 * ```tsx
 * // Simple spinner
 * <CustomSpinner />
 *
 * // Animated spinner with branding
 * <CustomSpinner
 *   variant="animated"
 *   showLogo
 *   primaryText="Loading document..."
 *   color="#1890ff"
 * />
 * ```
 */
export const CustomSpinner = ({
  variant = 'simple',
  width = 200,
  color = '#000',
  showLogo = false,
  primaryText,
  secondaryText,
  logo,
  style,
  className,
}: CustomSpinnerProps) => {
  if (variant === 'simple') {
    return (
      <Flex
        align="center"
        justify="center"
        style={style}
        className={className}
      >
        <Spin indicator={<LoadingOutlined style={{ color }} />} />
      </Flex>
    );
  }

  return (
    <Container style={style} className={className}>
      <AnimatedSpinner width={width} color={color} />
      {showLogo && (
        <CenteredContent gap={0}>
          {logo || <DefaultLogo />}
          {primaryText && (
            <Typography.Text style={{ opacity: 0.5 }}>
              {primaryText}
            </Typography.Text>
          )}
          {secondaryText && (
            <Typography.Text style={{ opacity: 0.5 }}>
              {secondaryText}
            </Typography.Text>
          )}
        </CenteredContent>
      )}
    </Container>
  );
};

export default CustomSpinner;
